import { useState, useEffect } from "react";
import api from "./api";
import { LANGUAGES, languageLabel as localLanguageLabel } from "./meta";

// Single cached fetch of the canonical public taxonomy (GET /api/taxonomy),
// shared across components. Local meta.js stays as a resilience-only fallback.
let _cache = null;
let _inflight = null;

export function loadTaxonomy() {
  if (_cache) return Promise.resolve(_cache);
  if (!_inflight) {
    _inflight = api.get("/taxonomy")
      .then((r) => { _cache = r.data; return _cache; })
      .catch((e) => { _inflight = null; throw e; });
  }
  return _inflight;
}

export function useTaxonomy() {
  const [tax, setTax] = useState(_cache);
  useEffect(() => {
    if (_cache) { setTax(_cache); return; }
    let mounted = true;
    loadTaxonomy().then((d) => mounted && setTax(d)).catch(() => {});
    return () => { mounted = false; };
  }, []);
  return tax;
}

// Fallback options shaped like taxonomy languages (native string == canonical value for legacy data).
const FALLBACK_LANGUAGE_OPTIONS = LANGUAGES.map((v) => ({ value: v }));

// Canonical public language options [{value, es?, en?}] — taxonomy first, local fallback for resilience.
export function useLanguageOptions() {
  const tax = useTaxonomy();
  return tax?.languages?.length ? tax.languages : FALLBACK_LANGUAGE_OPTIONS;
}

// Localized label resolver: taxonomy first, then local fallback
// (handles legacy display strings + unknown codes; never blank).
export function useLanguageLabeler() {
  const tax = useTaxonomy();
  return (value, lang) => {
    const def = tax?.languages?.find((l) => l.value === value);
    if (def) {
      if (lang === "es") return def.es;
      if (lang === "am") return def.am ?? def.en ?? def.es;
      return def.en ?? def.es;
    }
    return localLanguageLabel(value, lang);
  };
}

// Map a legacy native display string (or code) to its canonical language code.
const LEGACY_LANG_TO_CODE = {
  "Español": "es", "English": "en", "Português": "pt", "Français": "fr",
  "Italiano": "it", "Deutsch": "de", "Català": "ca", "Galego": "gl",
  "Euskara": "eu", "Guaraní": "gn", "Quechua": "qu",
};
export function canonicalLang(value) {
  return LEGACY_LANG_TO_CODE[value] || value;
}

// Equivalence-aware active check + toggle so legacy values light up correctly,
// selections store the canonical code, and no duplicate/mixed entries are produced.
export function langActive(arr, code) {
  return (arr || []).some((a) => canonicalLang(a) === code);
}
export function toggleLanguage(arr, code) {
  const list = arr || [];
  const has = list.some((a) => canonicalLang(a) === code);
  const cleaned = list.filter((a) => canonicalLang(a) !== code);
  return has ? cleaned : [...cleaned, code];
}
